import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, marketplaceAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);

  // Function to refresh session from AALIGN check login API
  const refreshSession = async (userId, role) => {
    try {
      const response = await marketplaceAPI.checkLogin(userId, role);
      if (response.data.success) {
        const userData = response.data.user;
        
        // Update connected users from fresh API response
        const connected = userData.connected_users || [];
        setConnectedUsers(connected);
        localStorage.setItem('connected_users', JSON.stringify(connected));
        
        // Update pending requests from fresh API response
        const pending = userData.pending_users || [];
        setPendingRequests(pending);
        localStorage.setItem('pending_requests', JSON.stringify(pending));
        
        // Update user data
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Update token if provided
        if (response.data.access_token) {
          localStorage.setItem('auth_token', response.data.access_token);
        }
        
        console.log('Session refreshed from AALIGN API');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to refresh session:', error);
      return false;
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('user');
      const savedConnectedUsers = localStorage.getItem('connected_users');
      const savedPendingRequests = localStorage.getItem('pending_requests');
      
      if (token && savedUser) {
        const parsedUser = JSON.parse(savedUser);
        
        // Set initial state from localStorage
        setUser(parsedUser);
        if (savedConnectedUsers) {
          setConnectedUsers(JSON.parse(savedConnectedUsers));
        }
        if (savedPendingRequests) {
          setPendingRequests(JSON.parse(savedPendingRequests));
        }
        
        // For AALIGN users (auto-login users), refresh session from check login API
        if (parsedUser.id && parsedUser.role && (!parsedUser.email || parsedUser.email.endsWith('@aalign.local'))) {
          await refreshSession(parsedUser.id, parsedUser.role);
        } else if (parsedUser.email && !parsedUser.email.endsWith('@aalign.local')) {
          // For traditional auth users, verify token
          try {
            const response = await authAPI.getMe();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          } catch (error) {
            // Token invalid for traditional users
            console.error('Token validation failed:', error);
          }
        }
      }
      
      setLoading(false);
    };
    
    initializeAuth();
  }, []);

  // Traditional email/password login (legacy - may be deprecated)
  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  // AALIGN auto-login
  const autoLogin = (loginResponse) => {
    const { access_token, user: userData } = loginResponse;
    
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Store connected users for conditional UI rendering
    const connected = userData.connected_users || [];
    localStorage.setItem('connected_users', JSON.stringify(connected));
    setConnectedUsers(connected);
    
    // Store pending users from AALIGN API
    const pending = userData.pending_users || [];
    localStorage.setItem('pending_requests', JSON.stringify(pending));
    setPendingRequests(pending);
    
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('connected_users');
    localStorage.removeItem('pending_requests');
    setUser(null);
    setConnectedUsers([]);
    setPendingRequests([]);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Check if user is connected with a specific buyer/seller
  const isConnectedWith = (userId) => {
    return connectedUsers.includes(userId);
  };

  // Check if a request is pending with a specific buyer/seller
  const isPendingWith = (userId) => {
    return pendingRequests.includes(userId);
  };

  // Add a new connected user (after successful business request approval)
  const addConnectedUser = (userId) => {
    if (!connectedUsers.includes(userId)) {
      const updated = [...connectedUsers, userId];
      setConnectedUsers(updated);
      localStorage.setItem('connected_users', JSON.stringify(updated));
    }
    // Remove from pending if it was there
    if (pendingRequests.includes(userId)) {
      const updatedPending = pendingRequests.filter(id => id !== userId);
      setPendingRequests(updatedPending);
      localStorage.setItem('pending_requests', JSON.stringify(updatedPending));
    }
  };

  // Add a pending request (after sending business request)
  const addPendingRequest = (userId) => {
    if (!pendingRequests.includes(userId) && !connectedUsers.includes(userId)) {
      const updated = [...pendingRequests, userId];
      setPendingRequests(updated);
      localStorage.setItem('pending_requests', JSON.stringify(updated));
    }
  };

  const value = {
    user,
    loading,
    login,
    autoLogin,
    register,
    logout,
    updateUser,
    refreshSession,
    connectedUsers,
    pendingRequests,
    isConnectedWith,
    isPendingWith,
    addConnectedUser,
    addPendingRequest,
    isAuthenticated: !!user,
    isBuyer: user?.role === 'buyer',
    isSeller: user?.role === 'seller',
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
